import { useCallback, useEffect, useRef, useState } from "react";
import { localDb, appDb, allCraftspacesDb } from "@/lib/localDb";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { subscribe } from "./queryCache";

type Row = Record<string, unknown>;

/**
 * Which database to route queries to:
 *   craftspaceId string  — a specific craftspace DB
 *   "all"               — aggregate across all craftspace DBs (read-only)
 *   undefined/null      — app.db (craftspaces registry)
 */
export type DbTarget = string | "all" | undefined | null;

function parseJsonFields(row: Row, fields: string[]): Row {
  const result = { ...row };
  for (const field of fields) {
    if (typeof result[field] === "string") {
      try {
        result[field] = JSON.parse(result[field] as string);
      } catch {
        // leave as string if parsing fails
      }
    }
  }
  return result;
}

function makeQueryFn(target: DbTarget) {
  if (target === "all") return allCraftspacesDb.query.bind(allCraftspacesDb);
  if (target === null) return appDb.query.bind(appDb);
  if (!target) return null; // undefined = skip
  return localDb(target).query.bind(localDb(target));
}

function makeGetFn(target: DbTarget) {
  if (target === "all") {
    // For get() with "all", run query and return first result
    return async (sql: string, params?: unknown[]) => {
      const rows = await allCraftspacesDb.query(sql, params);
      return rows[0];
    };
  }
  if (target === null) return appDb.get.bind(appDb);
  if (!target) return null; // undefined = skip
  return localDb(target).get.bind(localDb(target));
}

/**
 * Generic hook for a SQLite query that returns multiple rows.
 * Re-runs whenever `invalidateTables` are invalidated, `params` change, or `dbTarget` changes.
 *
 * @param dbTarget  Craftspace ID, "all" (aggregate), or undefined (app.db)
 */
export function useLocalQuery<T = Row>(
  sql: string,
  params: unknown[],
  invalidateTables: string[],
  jsonFields: string[] = [],
  dbTarget: DbTarget = undefined,
): T[] | undefined {
  const [data, setData] = useState<T[] | undefined>(undefined);

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const jsonFieldsRef = useRef(jsonFields);
  jsonFieldsRef.current = jsonFields;
  const invalidateTablesRef = useRef(invalidateTables);
  invalidateTablesRef.current = invalidateTables;

  // fetch is recreated when sql or dbTarget changes, triggering a re-fetch
  const fetch = useCallback(async () => {
    if (!isElectron()) return;
    const queryFn = makeQueryFn(dbTarget);
    if (!queryFn) return; // dbTarget is undefined → skip
    const rows = await queryFn<Row>(sql, paramsRef.current);
    setData(rows.map((r) => parseJsonFields(r, jsonFieldsRef.current) as T));
  }, [sql, dbTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when params values change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    if (!isElectron()) return;
    fetch();
  }, [fetch, paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to invalidation signals
  useEffect(() => {
    if (!isElectron()) return;
    const tables = invalidateTablesRef.current;
    const unsubscribers = tables.map((table) => subscribe(table, fetch));
    return () => unsubscribers.forEach((unsub) => unsub());
  }, [fetch]);

  return data;
}

/**
 * Generic hook for a SQLite query that returns a single row.
 *
 * @param dbTarget  Craftspace ID, "all" (aggregate), or undefined (app.db)
 */
export function useLocalGet<T = Row>(
  sql: string,
  params: unknown[],
  invalidateTables: string[],
  jsonFields: string[] = [],
  dbTarget: DbTarget = undefined,
): T | null | undefined {
  const [data, setData] = useState<T | null | undefined>(undefined);

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const jsonFieldsRef = useRef(jsonFields);
  jsonFieldsRef.current = jsonFields;
  const invalidateTablesRef = useRef(invalidateTables);
  invalidateTablesRef.current = invalidateTables;

  const fetch = useCallback(async () => {
    if (!isElectron()) return;
    const getFn = makeGetFn(dbTarget);
    if (!getFn) return; // dbTarget is undefined → skip
    const row = await getFn(sql, paramsRef.current);
    setData(row ? (parseJsonFields(row as Row, jsonFieldsRef.current) as T) : null);
  }, [sql, dbTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    if (!isElectron()) return;
    fetch();
  }, [fetch, paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isElectron()) return;
    const tables = invalidateTablesRef.current;
    const unsubscribers = tables.map((table) => subscribe(table, fetch));
    return () => unsubscribers.forEach((unsub) => unsub());
  }, [fetch]);

  return data;
}
